import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, Link as RouterLink } from 'react-router-dom'
import { AuthService } from 'crqlar-react-common/dist/services/auth/auth.service'
import {
    Box,
    Button,
    Snackbar,
    Alert,
    Stack,
    Typography,
    TextField,
    FormGroup,
    FormLabel,
} from '@mui/material'
import {jwtDecode} from "jwt-decode";

interface Props {}

const ChangePasswordForm = ({}: Props) => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()

    const [token, setToken] = useState<string | null>(null)
    const [invalidLink, setInvalidLink] = useState<boolean>(false)
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [confirmPassword, setConfirmPassword] = useState<string>('')
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true)
    const [success, setSuccess] = useState<boolean>(false)
    const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    })

    useEffect(() => {
        const tkn = searchParams.get('token')
        if (!tkn) {
            setInvalidLink(true)
        } else {
            setToken(tkn)

            try {
                const decoded: any = jwtDecode(tkn)
                if (decoded?.email) {
                    setEmail(decoded.email)
                }
            } catch (err) {
                console.error('Failed to decode JWT:', err)
                setInvalidLink(true)
            }
        }
    }, [searchParams])


    useEffect(() => {
        const emailValid = email.trim().length > 0
        const passwordValid = password.trim().length >= 6
        const passwordsMatch = password === confirmPassword
        setSubmitDisabled(!(emailValid && passwordValid && passwordsMatch))
    }, [email, password, confirmPassword])

    const handleChangePassword = async () => {
        if (!token) return
        try {
            await AuthService.changePassword(email.trim().toLowerCase(), password, token)
            setSuccess(true)
            setSnack({ open: true, message: t("Your password has been changed"), severity: 'success' })
        } catch {
            setSnack({ open: true, message: t('Failed to change password. Please try again'), severity: 'error' })
        }
    }

    const closeSnack = () => {
        setSnack(prev => ({ ...prev, open: false }))
    }

    if (invalidLink) {
        return (
            <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', p: 3 }}>
                <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
                    <Typography variant="h6" textAlign="center">
                        {t('Invalid or expired link')}
                    </Typography>
                    <Typography textAlign="center">
                        {t('Please request a new password reset link.')}
                    </Typography>
                    <Button variant="contained" component={RouterLink} to="/identity/forgotPassword">
                        {t('Request new link')}
                    </Button>
                </Stack>
            </Box>
        )
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', p: 3 }}>
            {success ? (
                <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
                    <Typography variant="h6" textAlign="center">
                        {t('Password changed')}
                    </Typography>
                    <Typography textAlign="center">
                        {t('You can now log in with your new password')}
                    </Typography>
                    <Button variant="contained" component={RouterLink} to="/identity/login">
                        {t('Login')}
                    </Button>
                </Stack>
            ) : (
                <Stack component="form" spacing={2} onSubmit={e => e.preventDefault()}>
                    <Typography variant="h6">{t('Change password')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('Enter your email and a new password below')}.
                    </Typography>

                    <FormGroup>
                        <FormLabel>{t('Email')}</FormLabel>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('New password')}</FormLabel>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('Confirm password')}</FormLabel>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            error={confirmPassword !== '' && confirmPassword !== password}
                            helperText={
                                confirmPassword !== '' && confirmPassword !== password
                                    ? t('Passwords do not match')
                                    : ''
                            }
                        />
                    </FormGroup>

                    <Button fullWidth variant="contained" onClick={handleChangePassword} disabled={submitDisabled}>
                        {t('Change Password')}
                    </Button>
                </Stack>
            )}

            <Snackbar open={snack.open} autoHideDuration={12000} onClose={closeSnack} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} >
                <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default ChangePasswordForm
